import { useEffect, useState } from "react";
import {
  Autocomplete, CircularProgress,
  FormControlLabel, FormHelperText, Stack, Switch, TextField,
} from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import * as api from "../../apiService/Core/countriesApi";
import type { CreateCountryBody, IsoCountry } from "./types";
import DarkDialog from "../../Components/Push/DarkDialog";
import { formFieldSx, menuPaperSx } from "../../Components/Push/pushStyles";
import { InlineAlert } from "../../Components/Push/PushDisabledNotice";

interface Props {
  open: boolean;
  nextSortOrder: number;
  onClose: () => void;
  onCreate: (body: CreateCountryBody) => Promise<unknown>;
}

export default function AddCountryDialog({ open, nextSortOrder, onClose, onCreate }: Props) {
  const [options, setOptions] = useState<IsoCountry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IsoCountry | null>(null);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [onboardingEnabled, setOnboardingEnabled] = useState(false);
  const [stateRequired, setStateRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null); setSignupEnabled(true); setOnboardingEnabled(false); setStateRequired(false); setError(null);
    let alive = true;
    setLoading(true);
    api.listAvailableCountries()
      .then((r) => alive && setOptions(r))
      .catch((e) => alive && setError(api.errorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await onCreate({ alpha2: selected.alpha2, signupEnabled, onboardingEnabled, stateRequired, sortOrder: nextSortOrder });
      onClose();
    } catch (e) {
      setError(api.errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DarkDialog
      open={open}
      onClose={onClose}
      title="Add country"
      icon={<PublicOutlinedIcon sx={{ fontSize: 18 }} />}
      busy={saving}
      confirmText="Add country"
      onConfirm={submit}
      confirmDisabled={!selected}
    >
      <Stack spacing={2}>
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
        <Autocomplete
          options={options}
          loading={loading}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          getOptionLabel={(option) => `${option.name} (${option.alpha2})`}
          isOptionEqualToValue={(option, value) => option.alpha2 === value.alpha2}
          slotProps={{ paper: { sx: menuPaperSx } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              sx={formFieldSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (<>{loading ? <CircularProgress size={18} sx={{ color: "#ff6a3d" }} /> : null}{params.InputProps.endAdornment}</>),
              }}
            />
          )}
        />
        <FormControlLabel
          sx={{ color: "#c0c0c0", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          control={<Switch checked={signupEnabled} onChange={(event) => setSignupEnabled(event.target.checked)} sx={{ "& .Mui-checked": { color: "#ff6a3d" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#ff6a3d" } }} />}
          label="Show in signup country list"
        />
        <div>
          <FormControlLabel
            sx={{ color: "#c0c0c0", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
            control={<Switch checked={onboardingEnabled} onChange={(event) => setOnboardingEnabled(event.target.checked)} sx={{ "& .Mui-checked": { color: "#ff6a3d" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#ff6a3d" } }} />}
            label="Allow as country of residence (onboarding)"
          />
          <FormHelperText sx={{ color: "#7a7a7a", ml: 1.5 }}>
            Leave off until KYC level, currency, loan provider and fee setup exist for this country.
          </FormHelperText>
        </div>
        <FormControlLabel
          sx={{ color: "#c0c0c0", "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          control={<Switch checked={stateRequired} onChange={(event) => setStateRequired(event.target.checked)} sx={{ "& .Mui-checked": { color: "#ff6a3d" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#ff6a3d" } }} />}
          label="Ask for a state at signup"
        />
      </Stack>
    </DarkDialog>
  );
}
